import React, { useState, useEffect } from 'react';
import { api, formatCurrency, formatDate, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Wind, Search, Plus, Edit, Trash2, Phone, MapPin, Check, Clock, AlertTriangle } from 'lucide-react';
import { useStore } from '../context/StoreContext';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Në pritje', color: 'secondary' },
  { value: 'scheduled', label: 'Planifikuar', color: 'default' },
  { value: 'in_progress', label: 'Në montim', color: 'warning' },
  { value: 'completed', label: 'Përfunduar', color: 'success' },
  { value: 'cancelled', label: 'Anuluar', color: 'destructive' },
];

const AC_TYPES = [
  'Split', 'Multi-Split', 'Portativ', 'Ceiling', 'Cassette', 'Ducted', 'VRV/VRF'
];

export default function AirConditioners() {
  const [installations, setInstallations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInstallation, setSelectedInstallation] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: '',
    customer_phone: '',
    customer_address: '',
    ac_type: 'Split',
    ac_brand: '',
    ac_model: '',
    ac_capacity: '',
    installation_address: '',
    scheduled_date: '',
    price: 0,
    paid: 0,
    notes: '',
    status: 'pending'
  });

  // Store context
  const { currentStore } = useStore();

  useEffect(() => {
    loadData();
  }, [currentStore]);

  const loadData = async () => {
    try {
      const storeParam = currentStore?.id ? `?store_id=${currentStore.id}` : '';
      const [installationsRes, customersRes] = await Promise.all([
        api.get(`/ac-installations${storeParam}`),
        api.get(`/customers${storeParam}`)
      ]);
      setInstallations(installationsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Gabim në ngarkimin e të dhënave');
    } finally {
      setLoading(false);
    }
  };

  const filteredInstallations = installations.filter(inst => {
    const matchesSearch = 
      inst.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
      inst.ac_brand?.toLowerCase().includes(search.toLowerCase()) ||
      inst.ac_model?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || inst.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      if (currentStore?.id) {
        submitData.store_id = currentStore.id;
      }
      if (selectedInstallation) {
        await api.put(`/ac-installations/${selectedInstallation.id}`, submitData);
        toast.success('Montimi u përditësua');
      } else {
        await api.post('/ac-installations', submitData);
        toast.success('Montimi u regjistrua');
      }
      setIsModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Gabim në ruajtje');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Jeni të sigurt?')) return;
    try {
      await api.delete(`/ac-installations/${id}`);
      toast.success('Montimi u fshi');
      loadData();
    } catch (error) {
      toast.error('Gabim në fshirje');
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const updateData = { status: newStatus };
      if (currentStore?.id) {
        updateData.store_id = currentStore.id;
      }
      await api.put(`/ac-installations/${id}`, updateData);
      toast.success('Statusi u përditësua');
      loadData();
    } catch (error) {
      toast.error('Gabim');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      customer_name: '',
      customer_phone: '',
      customer_address: '',
      ac_type: 'Split',
      ac_brand: '',
      ac_model: '',
      ac_capacity: '',
      installation_address: '',
      scheduled_date: '',
      price: 0,
      paid: 0,
      notes: '',
      status: 'pending'
    });
    setSelectedInstallation(null);
  };

  const openEditModal = (inst) => {
    setSelectedInstallation(inst);
    setFormData({
      customer_id: inst.customer_id || '',
      customer_name: inst.customer_name || '',
      customer_phone: inst.customer_phone || '',
      customer_address: inst.customer_address || '',
      ac_type: inst.ac_type || 'Split',
      ac_brand: inst.ac_brand || '',
      ac_model: inst.ac_model || '',
      ac_capacity: inst.ac_capacity || '',
      installation_address: inst.installation_address || '',
      scheduled_date: inst.scheduled_date?.split('T')[0] || '',
      price: inst.price || 0,
      paid: inst.paid || 0,
      notes: inst.notes || '',
      status: inst.status || 'pending'
    });
    setIsModalOpen(true);
  };

  const handleCustomerSelect = (customerId) => {
    if (customerId === 'new') {
      setFormData({ ...formData, customer_id: '', customer_name: '', customer_phone: '', customer_address: '' });
    } else {
      const customer = customers.find(c => c.id === customerId);
      if (customer) {
        setFormData({
          ...formData,
          customer_id: customer.id,
          customer_name: customer.name,
          customer_phone: customer.phone || '',
          customer_address: customer.address || '',
          installation_address: customer.address || formData.installation_address
        });
      }
    }
  };

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find(s => s.value === status);
    return <Badge variant={opt?.color || 'secondary'}>{opt?.label || status}</Badge>;
  };

  const pendingCount = installations.filter(i => i.status === 'pending' || i.status === 'scheduled').length;
  const inProgressCount = installations.filter(i => i.status === 'in_progress').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ac-page">
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{installations.length}</p>
            <p className="text-sm text-slate-500">Total</p>
          </CardContent>
        </Card>
        <Card className="border-yellow-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
            <p className="text-sm text-slate-500">Në pritje</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{inProgressCount}</p>
            <p className="text-sm text-slate-500">Në montim</p>
          </CardContent>
        </Card>
        <Card className="border-green-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">
              {installations.filter(i => i.status === 'completed').length}
            </p>
            <p className="text-sm text-slate-500">Përfunduar</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex flex-1 gap-4">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Kërko..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Të gjitha</SelectItem>
              {STATUS_OPTIONS.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => { resetForm(); setIsModalOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Montim i Ri
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Klienti</TableHead>
                <TableHead>Kondicioneri</TableHead>
                <TableHead>Adresa</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Çmimi</TableHead>
                <TableHead>Statusi</TableHead>
                <TableHead className="text-right">Veprime</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInstallations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    <Wind className="w-12 h-12 mx-auto mb-2 opacity-20" />
                    Nuk ka montime
                  </TableCell>
                </TableRow>
              ) : (
                filteredInstallations.map((inst) => (
                  <TableRow key={inst.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{inst.customer_name}</p>
                        {inst.customer_phone && (
                          <p className="text-xs text-slate-500">{inst.customer_phone}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{inst.ac_brand} {inst.ac_model}</p>
                        <p className="text-xs text-slate-500">{inst.ac_type} {inst.ac_capacity}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500 max-w-[150px] truncate">
                      {inst.installation_address || '-'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {inst.scheduled_date ? formatDate(inst.scheduled_date) : '-'}
                    </TableCell>
                    <TableCell className="font-mono">
                      {formatCurrency(inst.price)}
                      {inst.paid < inst.price && (
                        <span className="text-xs text-rose-500 block">
                          Borxh: {formatCurrency(inst.price - inst.paid)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(inst.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {inst.status !== 'completed' && inst.status !== 'cancelled' && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => updateStatus(inst.id, inst.status === 'in_progress' ? 'completed' : 'in_progress')}
                          >
                            <Check className="w-4 h-4 text-green-500" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => openEditModal(inst)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(inst.id)}>
                          <Trash2 className="w-4 h-4 text-rose-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Form Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedInstallation ? 'Modifiko Montimin' : 'Montim i Ri'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Customer Section */}
            <div className="border-b pb-4">
              <h4 className="font-medium mb-3">Klienti</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Zgjidh Klient</Label>
                  <Select value={formData.customer_id || 'new'} onValueChange={handleCustomerSelect}>
                    <SelectTrigger>
                      <SelectValue placeholder="Klient i ri" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">Klient i ri</SelectItem>
                      {customers.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Emri *</Label>
                  <Input
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Telefon</Label>
                  <Input
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* AC Details */}
            <div className="border-b pb-4">
              <h4 className="font-medium mb-3">Kondicioneri</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Lloji</Label>
                  <Select value={formData.ac_type} onValueChange={(v) => setFormData({ ...formData, ac_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AC_TYPES.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Kapaciteti (BTU)</Label>
                  <Input
                    value={formData.ac_capacity}
                    onChange={(e) => setFormData({ ...formData, ac_capacity: e.target.value })}
                    placeholder="12000"
                  />
                </div>
                <div>
                  <Label>Marka</Label>
                  <Input
                    value={formData.ac_brand}
                    onChange={(e) => setFormData({ ...formData, ac_brand: e.target.value })}
                    placeholder="Daikin, Mitsubishi..."
                  />
                </div>
                <div>
                  <Label>Modeli</Label>
                  <Input
                    value={formData.ac_model}
                    onChange={(e) => setFormData({ ...formData, ac_model: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Installation Details */}
            <div className="border-b pb-4">
              <h4 className="font-medium mb-3">Detajet e Montimit</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Adresa e Montimit</Label>
                  <Input
                    value={formData.installation_address}
                    onChange={(e) => setFormData({ ...formData, installation_address: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Data e Planifikuar</Label>
                  <Input
                    type="date"
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Statusi</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Çmimi (LEK)</Label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>Paguar (LEK)</Label>
                  <Input
                    type="number"
                    value={formData.paid}
                    onChange={(e) => setFormData({ ...formData, paid: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Shënime</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={2}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>Anulo</Button>
              <Button type="submit">Ruaj</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
